import {PlayerBadge, randomEnum, randomString, useGameServerConnection, usePlayers} from '@glark-newco/game-library';
import {Alert, Button, Group, Paper, SimpleGrid, Stack, Text, Title} from '@mantine/core';
import {IconInfoCircle} from '@tabler/icons-react';
import {shuffle} from 'lodash';
import {useCallback, useMemo} from 'react';
import {SupplyChainAvatarProvider} from './SupplyChainAvatar';
import {useNetworkLinks, useNetworkNodes, useNetworkOwnership} from '../state/networkHooks';
import {Link, NodeId, NodeOwnership, PositionNode} from '../state/networkStore';
import {
    AssignLinkEvent,
    AssignPlayerNodePayload,
    DisplayPlayScenePayload,
    RingColour,
    RingDefinition,
    SupplyChainPayload,
} from '../SupplyChainPayloadTypes';

// Max number of rings a player may hold across their belt and objective card.
export const playerCapacity = 4;

export function buildPayloads(nodes: Record<NodeId, PositionNode>, links: Link[], ownership: NodeOwnership): SupplyChainPayload[] {
    const actions: SupplyChainPayload[] = [];

    const ringDefinitions: RingDefinition[][] = [];
    const populatedNodes: Record<NodeId, PositionNode>  = Object.fromEntries(Object.entries(nodes)
        .filter(([, node]:[string, PositionNode]) => !!ownership[node.id])
        .map(([nodeId, node]) => [Number(nodeId), node]));

    for (let i = 1; i <= Object.keys(populatedNodes).length; i++) {
        // 1/3 of players have any empty "slot" to allow a ring to be passed.
        const numberOfRings = (i % 3 === 0) ? playerCapacity - 1 : playerCapacity;
        ringDefinitions.push(
            Array.from({length: numberOfRings}, () => ({
                ringId: randomString(6),
                colour: randomEnum(RingColour),
            } as RingDefinition)),
        );
    }

    // Make all objectives achievable by assigning rings in "tiers"
    const objectiveTiers: RingColour[][] = Array.from({length: playerCapacity}, (_, i) =>
        shuffle(ringDefinitions.filter(allocation => allocation.length > i).map(allocation => allocation[i].colour)),
    );

    const allocations = shuffle(ringDefinitions).map((allocation, idx) => ({
        rings: allocation.reverse(),
        objective: objectiveTiers.filter(tier => tier.length > idx).map(tier => tier[idx]),
    }));

    // Shuffle players to randomly distribute allocations;
    shuffle(Object.values(populatedNodes)).forEach((node: PositionNode, idx) => {
        const allocation = allocations[idx];
        actions.push({
            type: 'assignPlayerNode',
            playerId: ownership[node.id],
            x: node.x,
            y: node.y,
            rings: allocation.rings,
            objective: allocation.objective,
        } as AssignPlayerNodePayload);
    });

    const nodeIds = Object.keys(populatedNodes).map(nodeId => Number(nodeId));
    links.filter(link => nodeIds.includes(link.nodeIdA) && nodeIds.includes(link.nodeIdB))
        .forEach(link => {
            actions.push({
                type: 'assignLink',
                playerA: ownership[link.nodeIdA],
                playerB: ownership[link.nodeIdB],
                relationship: link.relationship,
            } as AssignLinkEvent);
        });

    return actions;
}

export function findConnectedNodes(populatedNodes: NodeId[], links: Link[]): NodeId[] {
    const populatedLinks = links.filter(link => populatedNodes.includes(link.nodeIdA) && populatedNodes.includes(link.nodeIdB));
    let connectedNodes: Set<NodeId> = populatedNodes.length > 0 ? new Set([populatedNodes[0]]) : new Set();
    let scanComplete = false;
    while (populatedLinks.length > 0 && !scanComplete) {
        const initialNodes = [...connectedNodes]; // work around closure danger highlighted by esLint
        const frontierLinks = populatedLinks
            .filter(link => initialNodes.includes(link.nodeIdA) || initialNodes.includes(link.nodeIdB));
        connectedNodes = new Set(frontierLinks.flatMap(l => [l.nodeIdA, l.nodeIdB]));
        scanComplete = connectedNodes.size === initialNodes.length;
    }
    return [...connectedNodes];
}

interface AssignPlayersPanelProps {
    onClose: () => void
}

export function AssignPlayersPanel({onClose: close}: AssignPlayersPanelProps) {
    const {publishPlayerAction} = useGameServerConnection<SupplyChainPayload>();
    const nodes = useNetworkNodes();
    const links = useNetworkLinks();
    const ownership = useNetworkOwnership();
    const {players} = usePlayers();

    const {populatedNodes, populatedPlayers} = useMemo(() => ({
        populatedNodes: Object.keys(ownership).map(Number),
        populatedPlayers: Object.values(ownership),
    }), [ownership]);

    const connectedNodes = useMemo(() => {
        return findConnectedNodes(populatedNodes, links);
    }, [links, populatedNodes]);

    const valid = populatedNodes.length >= 3 && populatedNodes.length === connectedNodes.length;

    const generateRingData = useCallback(() => {
        if (valid) {
            buildPayloads(nodes, links, ownership)
                .forEach(publishPlayerAction);
            publishPlayerAction({
                type: 'displayPlayScene',
            } as DisplayPlayScenePayload);
        }
    }, [nodes, links, ownership, publishPlayerAction]);

    // TODO - maybe check same player not in the network twice?

    return (
        <>
            <Title order={3} mb={'xl'}>Publish network to all players</Title>
            <Stack gap={'lg'}>
                {populatedNodes.length < 3 &&
                    <Alert variant="light" color="red" title="Please assign players" icon={<IconInfoCircle/>}>
                        Insufficient players have been allocated. Please allocate more players.  Supply Chain needs
                        at least 3 players but is most effective with 9 or more players.
                    </Alert>
                }
                {populatedNodes.length !== connectedNodes.length &&
                    <Alert variant="light" color="red" icon={<IconInfoCircle/>} title="Network is invalid">
                        Players should all be connected in a single network. Please move, remove or add players and
                        links to achieve a single network where all players are linked.
                    </Alert>
                }

                <Paper p="md" shadow="xl" withBorder>
                    <Text mb={'md'}>{populatedPlayers.length} player(s) will participate:</Text>
                    <SimpleGrid cols={4} spacing={'xs'}>
                        {players.filter(p => populatedPlayers.includes(p.playerId)).map(p =>
                            <PlayerBadge player={p} avatar={SupplyChainAvatarProvider} key={p.playerId}/>)}
                    </SimpleGrid>
                </Paper>

                {populatedPlayers.length < players.length &&
                    <Paper p="md" shadow="xl" withBorder>
                        <Text mb={'md'}>{players.length - populatedPlayers.length} player(s) will observe:</Text>
                        <SimpleGrid cols={4} spacing={'xs'}>
                            {players.filter(p => !populatedPlayers.includes(p.playerId)).map(p =>
                                <PlayerBadge player={p} avatar={SupplyChainAvatarProvider} key={p.playerId}/>)}
                        </SimpleGrid>
                    </Paper>
                }

                {valid &&
                    <Alert variant="light" color="blue" icon={<IconInfoCircle/>}
                        title="Are all players logged in and assigned in the network?">
                        You will not be able to make changes to add or remove players after you publish the network.
                    </Alert>
                }
                <Group justify={'right'} mt={'lg'}>
                    <Button disabled={!valid} onClick={() => generateRingData()}>Publish
                        network and generate objectives</Button>
                    <Button onClick={close} variant={'outline'}>Cancel</Button>
                </Group>
            </Stack>
        </>
    );
}
